import {
    type Application,
    FileName,
    type PageConfig,
    type File,
    type ReadAppParams,
    type ReadAppResult,
    type Parser
} from '@sap/ux-specification-types';
import { createApplicationAccess } from '@sap-ux/project-access';
import { create as createStorage } from 'mem-fs';
import { create } from 'mem-fs-editor';
import type { Editor } from 'mem-fs-editor';
import { getApiVersion, importProject } from '../api';
import { getModel } from './parser';

// Retrieve API version
const ApiVersion = getApiVersion();

/**
 * Returns a mem-fs editor instance. If an instance is not provided, a new one is created.
 *
 * @param {Editor} [fs] - An optional mem-fs editor instance.
 * @returns {Editor} - The mem-fs editor instance.
 */
export function getFsInstance(fs?: Editor): Editor {
    return fs ?? create(createStorage());
}

/**
 * Reads virtual files for a project located at the given application path.
 *
 * @param options - Parameters for application read.
 * @returns {Promise<ReadAppResult>} A promise that resolves to a ReadAppResult object containing an array of virtual `File` objects.
 */
export async function readApp(options: ReadAppParams): Promise<ReadAppResult> {
    const memFs = getFsInstance(options.fs);
    // Reuse app access or create new instance
    const appAccess = typeof options.app === 'string' ? await createApplicationAccess(options.app, memFs) : options.app;
    let files: File[] = [];
    const manifest = await appAccess.readManifest(memFs);
    if (manifest) {
        const annotationData = await appAccess.readAnnotationFiles(memFs);
        const changeFiles = await appAccess.readFlexChanges(memFs);
        // Import project using specification API
        files = await importProject({
            manifest: manifest,
            annotations: annotationData,
            flex: Object.values(changeFiles),
            logger: options.logger
        });
    }
    // Application model
    let applicationModel: Parser.ApplicationModel | undefined;
    if (!options.skipParsing) {
        const appFile = files.find((file) => file.dataSourceUri === FileName.App);
        const application: Application = appFile ? JSON.parse(appFile.fileContent) : {};
        const appSchemaFile = files.find((file) => application.$schema?.endsWith(file.dataSourceUri));
        if (application && appSchemaFile) {
            applicationModel = {
                ...application,
                model: getModel(appSchemaFile.fileContent, application),
                pages: {}
            };
            for (const pageId in application.pages ?? {}) {
                const page = application.pages[pageId];
                // Paths are unified and does not require OS specific handling
                const pageSourceUri = `pages/${pageId}.json`;
                const configFile = files.find((file) => file.dataSourceUri === pageSourceUri);
                const config: PageConfig = configFile ? JSON.parse(configFile.fileContent) : {};
                const schemaFile = files.find((file) => config.$schema?.endsWith(file.dataSourceUri));
                if (schemaFile) {
                    applicationModel.pages[pageId] = {
                        ...page,
                        model: getModel(schemaFile.fileContent, config, page.pageType)
                    };
                }
            }
        }
    }

    const result: ReadAppResult = {
        files,
        appAccess,
        version: ApiVersion
    };
    if (applicationModel) {
        result.applicationModel = applicationModel;
    }
    return result;
}
